import { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Minimize2, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import {
  formatYaml,
  jsonToYaml,
  minifyYaml,
  yamlToJson,
  type YamlResult,
} from '../lib/yaml';
import { DualPaneFormatter } from '../components/DualPaneFormatter';
import { ToolHeader } from '../components/ToolHeader';
import { consumeToolSeed } from '../lib/seedTransfer';

/**
 * `/tools/format/yaml` — YAML formatter / minifier + YAML ↔ JSON.
 *
 * Layout: shared `<DualPaneFormatter>` (left: yaml input, right: yaml
 * output OR json output depending on the active mode). The mode
 * pillbox in the toolbar drives the conversion.
 *
 * Mode | Input lang | Output lang | Pipeline
 * -----|------------|-------------|--------------------------
 * format     | yaml | yaml | formatYaml(input, indent)
 * minify     | yaml | yaml | minifyYaml(input)
 * yaml→json  | yaml | json | yamlToJson(input, indent)
 * json→yaml  | json | yaml | jsonToYaml(input, indent)
 *
 * Indent applies to the *output* in all four modes (json indent in
 * yaml→json, yaml indent everywhere else).
 */

type Mode = 'format' | 'minify' | 'yaml2json' | 'json2yaml';

const VALID_MODES: ReadonlySet<Mode> = new Set(['format', 'minify', 'yaml2json', 'json2yaml']);

export default function YamlTool(): JSX.Element {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>('format');
  const [indent, setIndent] = useState<2 | 4>(2);

  // Pick up a one-shot seed dropped by another tool (e.g. JsonTool's
  // "在 YAML 工具中打开"). Consume happens on mount and is one-way:
  // refreshing the page won't replay the JSON the user used to be on.
  // The seed always wins over the empty default; if the user clicked
  // "open here" they want to land on JSON → YAML mode pre-filled.
  useEffect(() => {
    const seed = consumeToolSeed('/tools/format/yaml');
    if (!seed) return;
    setInput(seed.text);
    if (seed.mode && VALID_MODES.has(seed.mode as Mode)) {
      setMode(seed.mode as Mode);
    }
  }, []);

  const result: YamlResult = useMemo(() => {
    switch (mode) {
      case 'format':
        return formatYaml(input, { indent });
      case 'minify':
        return minifyYaml(input);
      case 'yaml2json':
        return yamlToJson(input, { indent });
      case 'json2yaml':
        return jsonToYaml(input, { indent });
    }
  }, [input, mode, indent]);

  const inputLang = mode === 'json2yaml' ? 'json' : 'yaml';
  const outputLang =
    mode === 'yaml2json' ? 'json' : mode === 'json2yaml' ? 'yaml' : 'yaml';
  const outputFilename =
    mode === 'yaml2json' ? 'output.json' : 'output.yaml';

  const placeholder = useMemo(() => {
    if (mode === 'json2yaml') {
      return '粘贴 JSON 文本…\n例如：{"name":"demo","ports":[80,443]}';
    }
    return [
      '粘贴 YAML 文本…',
      '例如：',
      'apiVersion: v1',
      'kind: Pod',
      'metadata:',
      '  name: demo',
    ].join('\n');
  }, [mode]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-4">
      <ToolHeader
        toolId="yaml"
        category="format"
        description="YAML 美化 / 压缩（flow style）/ 与 JSON 双向互转。错误位置精确到行列号，输入区直接红线标记。全部纯前端，输入不上传。"
      />
      <DualPaneFormatter
        language={inputLang}
        outputLanguage={outputLang}
        inputValue={input}
        onInputChange={setInput}
        inputPlaceholder={placeholder}
        outputValue={result.ok ? result.output : ''}
        outputFilename={outputFilename}
        error={result.ok ? undefined : { message: result.error, line: result.line, column: result.column }}
        toolbar={
          <>
            <div className="inline-flex rounded-md border border-line">
              <ModeButton current={mode} value="format" onClick={setMode}>
                <Sparkles className="h-3.5 w-3.5" />
                美化
              </ModeButton>
              <ModeButton current={mode} value="minify" onClick={setMode}>
                <Minimize2 className="h-3.5 w-3.5" />
                压缩
              </ModeButton>
              <ModeButton current={mode} value="yaml2json" onClick={setMode}>
                <ArrowRightLeft className="h-3.5 w-3.5" />
                YAML → JSON
              </ModeButton>
              <ModeButton current={mode} value="json2yaml" onClick={setMode}>
                <ArrowRightLeft className="h-3.5 w-3.5 rotate-180" />
                JSON → YAML
              </ModeButton>
            </div>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <span>缩进</span>
              <select
                className="select py-0.5"
                value={indent}
                onChange={(e) => setIndent(Number(e.target.value) as 2 | 4)}
                disabled={mode === 'minify'}
                title="输出缩进空格数（压缩模式不生效）"
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
              </select>
            </label>
          </>
        }
      />
    </div>
  );
}

function ModeButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Mode;
  value: Mode;
  onClick: (m: Mode) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={clsx(
        'inline-flex items-center gap-1 px-2.5 py-0.5 text-[11.5px] font-medium transition first:rounded-l-md last:rounded-r-md',
        active ? 'bg-accent/10 text-accent' : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
